import { ConfirmModal } from '@client/components/_ui/feedback/confirm-modal';
import { useInitMessage } from '@client/components/_ui/feedback/message';
import { useInitModal } from '@client/components/_ui/feedback/modal';
import { useInitNotification } from '@client/components/_ui/feedback/notification';
import { theme as baseTheme, tokenValues } from '@client/design-system/__generated__';
import { useThemeStore } from '@client/stores/themeStore';
import '@client/styles/main.css';
import type { ThemeConfig } from 'antd';
import { App, theme as antdTheme, ConfigProvider } from 'antd';
import clsx from 'clsx';
import { type PropsWithChildren, useMemo } from 'react';

function UISetup({ children }: PropsWithChildren) {
    useInitMessage();
    useInitNotification();
    useInitModal();

    return (
        <>
            {children}
            <ConfirmModal />
        </>
    );
}

export function UIProvider({ children }: PropsWithChildren) {
    const resolvedTheme = useThemeStore((state) => state.resolvedTheme);
    const isDark = resolvedTheme === 'dark';

    // Merge base theme with dark algorithm when in dark mode
    const theme = useMemo<ThemeConfig>(() => {
        if (!isDark) {
            return baseTheme;
        }

        return {
            ...baseTheme,
            algorithm: antdTheme.darkAlgorithm,
            token: {
                ...baseTheme.token,
                // Dark mode color overrides for Ant Design (from design tokens)
                colorText: tokenValues.dark.semantic.foreground,
                colorTextSecondary: tokenValues.dark.semantic.mutedForeground,
                colorTextTertiary: tokenValues.dark.semantic.subtleForeground,
                colorTextQuaternary: tokenValues.dark.semantic.quaternaryForeground,
                colorTextPlaceholder: tokenValues.dark.semantic.placeholderForeground,
                colorBgContainer: tokenValues.dark.semantic.card,
                colorBgLayout: tokenValues.dark.semantic.background,
                colorBgElevated: tokenValues.dark.semantic.popover,
                colorBorder: tokenValues.dark.semantic.separator,
                colorBorderSecondary: tokenValues.dark.semantic.separatorSecondary,
            },
        };
    }, [isDark]);

    return (
        <ConfigProvider
            theme={theme}
            modal={{
                mask: {
                    blur: false,
                },
            }}
            drawer={{
                mask: {
                    blur: false,
                },
            }}
            form={{
                requiredMark(label, info) {
                    return info.required ? (
                        <span
                            className={clsx(
                                "pl-2.5 before:mr-1 before:text-ui-error before:text-xs before:content-['*']",
                                'before:absolute before:-translate-x-2.5 before:translate-y-0.5',
                            )}
                        >
                            {label}
                        </span>
                    ) : (
                        label
                    );
                },
            }}
        >
            <App className="flex min-h-screen" message={{ maxCount: 1 }}>
                <UISetup>{children}</UISetup>
            </App>
        </ConfigProvider>
    );
}
